import React, { useState } from 'react';
import Datetime from 'react-datetime';
import { Global } from '@emotion/core';
import { css } from 'emotion';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  Path,
} from 'react-hook-form';
import {
  Type,
  ButtonSecondary,
  IconChevronDown,
  ButtonPrimary,
} from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { Container, DatetimeGlobalStyles } from './styles';
import {
  getDisplayValue,
  roundToNextHour,
  getCurrentDateAtUpcomingHour,
} from './utils';

export const ControlledFieldDatePicker = <T extends FieldValues>({
  label,
  name = 'publishOn' as Path<T>,
  className,
  control,
}: {
  label: string;
  name: FieldPath<T>;
  className?: any;
  control?: Control<T>;
}) => {
  const [isShowingSchedulerModal, setIsShowingSchedulerModal] = useState(false);
  const {
    field: { value, onChange },
  } = useController<T, FieldPath<T>>({ control, name });
  const [valueOnOpen, setValueOnOpen] = useState(value);

  const dateTimeValue =
    !value || value === '' ? getCurrentDateAtUpcomingHour() : value;

  return (
    <Container className={className}>
      <Type
        as="span"
        weight="bold"
        aria-label={name}
        className={css`
          margin-bottom: 8px;
        `}
      >
        {label}
      </Type>
      <ButtonSecondary
        onClick={() => {
          setIsShowingSchedulerModal(true);
          setValueOnOpen(value);
        }}
        iconTrailing={IconChevronDown}
        className={css`
          border-radius: 4px;
          background: var(--background-base);
          ::after {
            border-radius: 4px;
          }
        `}
        type="button" // Don't want button to submit form
      >
        {getDisplayValue(value, null, 'Now')}
      </ButtonSecondary>
      <Modal
        aria-labelledby="schedule-publish-title"
        isShowing={isShowingSchedulerModal}
        isShowingCloseButton={true}
        onClose={() => {
          onChange(valueOnOpen);
          setIsShowingSchedulerModal(false);
        }}
      >
        <Modal.Title id="schedule-publish-title">
          {getDisplayValue(value, 'Schedule for', 'Publish now')}
        </Modal.Title>
        <Modal.Body
          as="div"
          className={css`
            width: 100%;
          `}
        >
          <Global styles={DatetimeGlobalStyles} />
          <Datetime
            className="DatetimeContainer"
            input={false}
            timeConstraints={{
              minutes: { min: 0, max: 0, step: 60 },
              seconds: { min: 0, max: 0, step: 60 },
              milliseconds: { min: 0, max: 0, step: 1000 },
            }}
            onChange={date => {
              onChange(roundToNextHour(date));
            }}
            value={dateTimeValue}
          />
          All times are local
        </Modal.Body>
        <Modal.Footer>
          <ButtonSecondary
            onClick={() => {
              onChange(null);
              setIsShowingSchedulerModal(false);
            }}
          >
            Reset to now
          </ButtonSecondary>
          <ButtonPrimary onClick={() => setIsShowingSchedulerModal(false)}>
            Confirm
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
